import {
  getSummaryStatsApi,
  getReviewFormApi,
  updateQuestionResponseApi,
  getPeerFeedbackApi,
  submitReviewApi,
  getActiveReviewsApi,
  getArchivedReviewsApi,
  getTeamReviewsApi,
  getRequestedReviewsApi,
  getReviewProvidersApi,
  acknowledgeReviewApi,
  getPeerPoolApi,
  nominatePeerApi,
  acceptNominationApi,
  rejectNominationApi,
  getPeerStatusApi,
  getUpwardStatusApi,
  getActiveReviewsCountApi,
  getPastFeedbacksApi,
  getReviewHelpDocumentsApi,
  getReviewHelpFAQsApi,
  getReviewHelpTimelineApi,
  publishReview,
  unPublishReview,
  getReviewGoalsApi,
  getReviewFormsListApi,
  getReviewFormApiV1,
  getReviewResponseApiV1,
  withdrawNominationApi,
  getReviewPeriodSummaryGoalsApi,
  getPerformancePreviewApi,
  updateReviewGoalsWeightageApi,
  getTextAnalysisApi,
  getFormatFeedbackFormTextApi,
  getAIManagerSummaryPointsApi,
  postAIManagerSummaryFeedbackApi,
  nominateDLMApi,
  withdrawDLMNominationApi,
  getAllNominationsApi,
  getReviewHelpTimelineApiV1,
} from 'api/performanceReview';
import Timeline from 'models/admin/timeline';
import ColorCodedDate from 'models/common/ColorCodedDate';
import Employee, { BasicEmployee } from 'models/employee';
import HashTag from 'models/hashtag';
import Review, { ReviewForm } from 'models/performanceReview/Review';
import ReviewQuestion, { AnswerOption } from 'models/reviewQuestion';
import Task1 from 'models/task1';

import { RATINGS_TYPES, REVIEW_FORMS_TYPE_MAP } from 'constants/ProjectConstants';
import { objectFlip } from 'constants/helper';
import { sendAnsweredReviewQuestionEvent } from 'constants/mixpanelHelpers/reviewsEvents';

import { PerformancePreviewGridBox } from './analytics/reviewHelper';

export const getBehaviorForm = ({
  reviewId,
  employeeId,
  providerId,
  responseId,
  providerType,
  isPreview,
  previewSubmissionStatus,
}) =>
  getReviewFormApi({
    formName: REVIEW_FORMS_TYPE_MAP.COMPETENCY,
    reviewId,
    employeeId,
    providerId,
    responseId,
    providerType,
    isPreview,
    previewSubmissionStatus,
  }).then((res) => {
    const mappedResponse = res.entity.competencyQuestions.map(({ competency, questions }) => {
      const tag = new HashTag();

      tag.fromJSON(competency.orgTag);
      tag.addStatsFromJSON(competency.stats);

      return {
        tag,
        questions: questions.map((question) => ReviewQuestion.fromJSON(question)),
      };
    });
    return {
      competencyQuestions: mappedResponse,
      averageCompetencyRating: res.entity.averageSectionRating,
      averageCompetencyRatingText: res.entity.averageSectionRatingText,
      maxCompetencyRating: res.entity.maxSectionRating,
      ratingMap: res.entity.ratingMap,
    };
  });

export const getGoalForm = ({
  reviewId,
  employeeId,
  providerId,
  responseId,
  providerType,
  isPreview,
  previewSubmissionStatus,
}) =>
  getReviewFormApi({
    formName: REVIEW_FORMS_TYPE_MAP.GOAL,
    reviewId,
    employeeId,
    providerId,
    responseId,
    providerType,
    isPreview,
    previewSubmissionStatus,
  }).then((res) => {
    const mappedResponse = res.entity.goalFormResponse.goalQuestions.map(({ goal, question, okrWeightageItem }) => ({
      goal: goal ? Task1.fromJSON(goal) : null,

      okr: okrWeightageItem
        ? {
            ...okrWeightageItem?.computedData,
            parentGoalBasicDetails: okrWeightageItem?.parentGoalBasicDetails,
            weightage: okrWeightageItem?.weightage,
          }
        : null,
      // to make it consistent with the COMPETENCY response
      questions: [question].map((q) => ReviewQuestion.fromJSON(q)),
    }));
    return {
      goalQuestions: mappedResponse,
      averageGoalRating: res.entity.averageSectionRating,
      averageGoalRatingText: res.entity.averageSectionRatingText,
      maxGoalRating: res.entity.maxSectionRating,
      ratingMap: res.entity.ratingMap,
      okrTotalWeight: res.entity.goalFormResponse.okrTotalWeight,
      isOkrChangeWeightageAllowed: res.entity.goalFormResponse.isOkrChangeWeightageAllowed,
    };
  });

export const getQAForm = ({
  reviewId,
  employeeId,
  providerId,
  responseId,
  providerType,
  isPreview,
  previewSubmissionStatus,
}) =>
  getReviewFormApi({
    formName: REVIEW_FORMS_TYPE_MAP.QNA,
    reviewId,
    employeeId,
    providerId,
    responseId,
    providerType,
    isPreview,
    previewSubmissionStatus,
  }).then((res) => {
    const mappedResponse = res.entity.qnaQuestions.map((question) => ReviewQuestion.fromJSON(question));
    return {
      QNAQuestions: mappedResponse,
      averageQNARating: res.entity.averageSectionRating,
      averageQNARatingText: res.entity.averageSectionRatingText,
      maxQNARating: res.entity.maxSectionRating,
      ratingMap: res.entity.ratingMap,
    };
  });

export const updateQuestionResponse = ({
  reviewId,
  goalId,
  competencyId,
  employeeId,
  questionId,
  feedbackText = '',
  feedbackTextHtml = '',
  selectedAnswerOptions = [],
  sourceForm = '',
  isPreview,
}) =>
  updateQuestionResponseApi({
    reviewId,
    goalId,
    competencyId,
    employeeId,
    responseBody: {
      id: questionId,
      selectedAnswerOptions: selectedAnswerOptions.map((opt) => ({ id: opt.id })),
      feedbackText,
      feedbackTextHtml,
    },
    isPreview,
  }).then((res) => {
    sendAnsweredReviewQuestionEvent({
      eventProperties: {
        reviewCycleId: reviewId,
        questionId,
        answerOptions: selectedAnswerOptions,
        feedbackText: !!feedbackText,
        feedbackTextHtml: !!feedbackTextHtml,
        section: sourceForm,
      },
    });
    return res.entity;
  });

export const getSummaryStats = ({
  reviewId,
  employeeId,
  providerId,
  useOldEndpoint = false,
  isPreview,
  providerType,
  previewSubmissionStatus,
}) =>
  getSummaryStatsApi({
    reviewId,
    employeeId,
    providerId,
    useOldEndpoint,
    isPreview,
    providerType,
    previewSubmissionStatus,
  }).then((res) => {
    const employee = new Employee();
    employee.fromJSON(res.entity.employee);

    return {
      ...res.entity,
      review: Review.fromJSON(res.entity.reviewInfo),
      employee,
      reviewForms: res.entity.formNames.map((fn) => ReviewForm.fromJSON(fn)),
    };
  });

export const getActiveReviews = ({ limit = 5, offset = 0, includeCount = false }) =>
  getActiveReviewsApi({ limit, offset, includeCount }).then((res) => ({
    totalCount: res.entity.totalCount,
    reviews: res.entity.data.map(({ employee, review, endDateFormatted, message, deadline, startDate }) => ({
      endDateFormatted: endDateFormatted ? ColorCodedDate.fromJSON(endDateFormatted) : null,
      employee: BasicEmployee.fromJSON(employee),
      review: Review.fromJSON(review),
      message,
      deadline,
      startDate,
    })),
  }));

export const getArchivedReviews = ({ limit = 5, offset = 0, includeCount = false }) =>
  getArchivedReviewsApi({ limit, offset, includeCount }).then((res) => ({
    totalCount: res.entity.totalCount,
    reviews: res.entity.data.map(({ employee, review, endDateFormatted }) => ({
      endDateFormatted: endDateFormatted ? ColorCodedDate.fromJSON(endDateFormatted) : null,
      employee: BasicEmployee.fromJSON(employee),
      review: Review.fromJSON(review),
    })),
  }));

export const getTeamReviews = ({ requestedReviewCycle = null }) =>
  getTeamReviewsApi({ requestedReviewCycle }).then((res) => {
    const { membersResponse } = res.entity;
    const dataRow = Object.values(membersResponse).map((value) => ({
      EMPLOYEE: BasicEmployee.fromJSON(value.employee),
      feedbackFormId: value.feedbackFormId,
      ACKNOWLEDGEMENT: value.acknowledgementStatus,
      PUBLISHED: value.publishStatus,
      isClickable: value.isClickable,
      containsReview: value.containsReview,
      isManagerTimelineStarted: value.isManagerTimelineStarted,
      ...value.reviews,
    }));
    return {
      enabledStages: res.entity.enabledStages,
      totalCount: res.entity.totalCount,
      reviewCycleId: res.entity.reviewCycleId,
      membersResponse: dataRow,
    };
  });

export const getRequestedReviews = ({ limit = 500, offset = 0, includeCount = false }) =>
  getRequestedReviewsApi({ limit, offset, includeCount }).then((res) => ({
    totalCount: res.entity.totalCount,
    reviews: res.entity.data.map(({ employee, review, endDateFormatted }) => ({
      endDateFormatted: endDateFormatted ? ColorCodedDate.fromJSON(endDateFormatted) : null,
      employee: BasicEmployee.fromJSON(employee),
      review: Review.fromJSON(review),
    })),
  }));

export const getPeerFeedback = ({
  goalId,
  competencyId,
  questionId,
  reviewId,
  employeeId,
  providerType,
  isPreview,
  previewSubmissionStatus,
}) =>
  getPeerFeedbackApi({
    goalId,
    competencyId,
    questionId,
    reviewId,
    employeeId,
    providerType,
    isPreview,
    previewSubmissionStatus,
  }).then((res) =>
    res.entity.map((feedback) => ({
      employee: BasicEmployee.fromJSON(feedback.employee),
      selectedAnswerOptions: feedback.selectedAnswerOptions.map((opt) => AnswerOption.fromJSON(opt)),
      feedbackText: feedback.feedbackText,
      feedbackTextHtml: feedback.feedbackTextHtml,
      providerType: feedback.providerType,
      peerType: feedback.peerType,
    }))
  );

export const submitReview = ({ reviewId, employeeId }) =>
  submitReviewApi({ reviewId, employeeId }).then((res) => ({
    reviewForms: res.entity.formNames.map((fn) => ReviewForm.fromJSON(fn)),
    review: Review.fromJSON(res.entity.response),
    message: res.message,
  }));

export const getReviewProviders = ({ reviewId, employeeId, providerType, isPreview, previewSubmissionStatus }) =>
  getReviewProvidersApi({
    reviewId,
    employeeId,
    providerType,
    isPreview,
    previewSubmissionStatus,
  }).then((res) =>
    res.entity.map(({ employee, review, endDateFormatted, isAccessible, accessReasonText }) => ({
      endDateFormatted: endDateFormatted ? ColorCodedDate.fromJSON(endDateFormatted) : null,
      employee: employee ? BasicEmployee.fromJSON(employee) : null,
      review: Review.fromJSON(review),
      isAccessible,
      accessReasonText,
    }))
  );

export const acknowledgeReview = ({ reviewId, providerId = null }) =>
  acknowledgeReviewApi({ reviewId, providerId }).then((res) => res.entity);

/*
  Using SummaryStatsApi for getting updated status of reviewForms.
*/
export const getUpdatedReviewForms = ({ reviewId, employeeId }) =>
  getSummaryStatsApi({
    reviewId,
    employeeId,
  }).then((res) => res.entity.formNames.map((fn) => ReviewForm.fromJSON(fn)));

// /*
//   Peer review
// */
// export const getNominatedPeers = ({ reviewId, empId }) => {
//   return getNominatedPeersApi({
//     reviewId,
//     empId
//   }).then((res) => {
//     return res.entity;
//   });
// };

export const getAllNominations = ({ reviewId, empId }) =>
  getAllNominationsApi({
    reviewId,
    empId,
  }).then((res) => res.entity);

// export const getDLMNominatedPeers = ({ reviewId }) => {
//   return getDLMNominatedPeersApi({
//     reviewId
//   }).then((res) => {
//     return res.entity;
//   });
// };

export const getPeerPool = ({ reviewId, empId }) =>
  getPeerPoolApi({
    reviewId,
    empId,
  }).then((res) => {
    if (!res.entity) {
      return null;
    }
    if (!res.entity.isDLMNominationPermitted) {
      return res.entity;
    }
    return {
      ...res.entity,
      peerLimits: {
        ...res.entity.peerLimits,
        // Although the dotted line manager is not technically a peer in BE,
        // in FE the objects withing peerLimits are treated as that many sections of Nomination to be done
        // so appending it like this here,
        // NOTE: for the nominations though the peerType will be null for DLM
        DLM: {
          minPeers: res.entity.minDLMReviewers ?? 0,
          maxPeers: res.entity.maxDLMReviewers ?? 100,
        },
      },
    };
  });

export const nominatePeer = ({ reviewId, employees, empId }) =>
  nominatePeerApi({
    reviewId,
    employees,
    empId,
  }).then((res) => {
    if (!res.entity) {
      return null;
    }
    return res.entity;
  });

export const nominateDLM = ({ reviewId, empId, dlmEmpId }) =>
  nominateDLMApi({
    reviewId,
    empId,
    dlmEmpId,
  }).then((res) => {
    if (!res.entity) {
      return null;
    }
    return res.entity;
  });

export const withdrawDLMNomination = ({ reviewId, empId, dlmEmpId }) =>
  withdrawDLMNominationApi({
    reviewId,
    empId,
    dlmEmpId,
  }).then((res) => {
    if (!res.entity) {
      return null;
    }
    return res.entity;
  });

export const acceptNomination = ({ reviewId, employeeId, isDLM = false }) =>
  acceptNominationApi({ reviewId, employeeId, isDLM }).then((res) => res);

export const rejectNomination = ({ reviewId, employeeId, isDLM = false }) =>
  rejectNominationApi({ reviewId, employeeId, isDLM }).then((res) => res);

export const withdrawNomination = ({ reviewId, providerEmpId, empId }) =>
  withdrawNominationApi({ reviewId, providerEmpId, empId }).then((res) => res);

export const getPeerStatus = () =>
  getPeerStatusApi().then((res) =>
    res.entity.map(({ employee, review, endDateFormatted }) => ({
      endDateFormatted: endDateFormatted ? ColorCodedDate.fromJSON(endDateFormatted) : null,
      employee: BasicEmployee.fromJSON(employee),
      review: Review.fromJSON(review),
    }))
  );

export const getUpwardStatus = () =>
  getUpwardStatusApi().then((res) =>
    res.entity.map(({ employee, review, endDateFormatted }) => ({
      endDateFormatted: endDateFormatted ? ColorCodedDate.fromJSON(endDateFormatted) : null,
      employee: BasicEmployee.fromJSON(employee),
      review: Review.fromJSON(review),
    }))
  );

export const getActiveReviewsCount = () => getActiveReviewsCountApi().then((res) => res.entity);

export const getPastFeedbacks = ({ offset = 0, limit = 10, includeCount = false, receiverEmployeeId }) =>
  getPastFeedbacksApi({ offset, limit, includeCount, receiverEmployeeId }).then((res) => ({
    totalCount: res.entity.totalCount,
    reviews: res.entity.data.map(({ employee, review, endDateFormatted, message }) => ({
      endDateFormatted: endDateFormatted && ColorCodedDate.fromJSON(endDateFormatted),
      employee: BasicEmployee.fromJSON(employee),
      review: Review.fromJSON(review),
      message,
    })),
  }));

export const getReviewHelpDocuments = ({ reviewId }) =>
  getReviewHelpDocumentsApi({ reviewId }).then((res) => res.entity);

export const getReviewHelpFAQs = ({ reviewId }) => getReviewHelpFAQsApi({ reviewId }).then((res) => res.entity);

export const getReviewHelpTimeline = ({ reviewId }) =>
  getReviewHelpTimelineApi({ reviewId }).then((res) =>
    res.entity.map((json) => {
      const timeline = new Timeline();
      timeline.fromJSON(json);
      return timeline;
    })
  );

export const getReviewHelpTimelineV1 = ({ reviewCycleId }) =>
  getReviewHelpTimelineApiV1({ reviewCycleId }).then((res) => res.entity);

export const publishUnpublishReviews = ({ reviewCycleId, empId, type, cohortId }) => {
  if (type === 'PUBLISH') return publishReview({ reviewCycleId, empId, cohortId }).then((res) => res.entity);
  return unPublishReview({ reviewCycleId, empId, cohortId }).then((res) => res.entity);
};

export const getReviewGoals = ({ reviewId, providerId, employeeId }) =>
  getReviewGoalsApi({ reviewId, providerId, employeeId }).then((res) => {
    const data = res.entity;
    return data ? data.map((taskJSON) => Task1.fromJSON(taskJSON)) : [];
  });

// rabbit

export const getReviewFormsList = ({ limit = 100, offset = 0, reviewCycleId, sectionId, includeCount = true }) =>
  getReviewFormsListApi({
    limit,
    offset,
    reviewCycleId,
    sectionId,
    includeCount,
  }).then((res) => res.entity);

export const getReviewResponseResultV1 = ({
  goalId,
  competencyId,
  questionId,
  reviewId,
  employeeId,
  excludeEmployeeId,
  providerType,
  isPreview,
  previewSubmissionStatus,
}) =>
  getReviewResponseApiV1({
    goalId,
    competencyId,
    questionId,
    reviewId,
    employeeId,
    excludeEmployeeId,
    providerType,
    isPreview,
    previewSubmissionStatus,
  }).then((res) =>
    res.entity.map((feedback) => ({
      employee: feedback.employee ? BasicEmployee.fromJSON(feedback.employee) : null,
      selectedAnswerOptions: feedback.selectedAnswerOptions.map((opt) => AnswerOption.fromJSON(opt)),
      feedbackText: feedback.feedbackText,
      feedbackTextHtml: feedback.feedbackTextHtml,
      providerType: feedback.providerType,
      peerType: feedback.peerType,
    }))
  );

export const getGoalFormV1 = ({ reviewId, employeeId, providerType, isPreview, previewSubmissionStatus }) =>
  getReviewFormApiV1({
    formName: REVIEW_FORMS_TYPE_MAP.GOAL,
    reviewId,
    employeeId,
    providerType,
    isPreview,
    previewSubmissionStatus,
  }).then((res) => {
    const mappedResponse = res.entity.goalFormResponse.goalQuestions.map(({ goal, question, okrWeightageItem }) => ({
      goal: goal ? Task1.fromJSON(goal) : null,
      okr: okrWeightageItem
        ? {
            ...okrWeightageItem?.computedData,
            parentGoalBasicDetails: okrWeightageItem?.parentGoalBasicDetails,
            weightage: okrWeightageItem?.weightage,
          }
        : null,
      // to make it consistent with the COMPETENCY response
      questions: [question].map((q) => ReviewQuestion.fromJSON(q)),
    }));
    return {
      goalQuestions: mappedResponse,
      averageGoalRating: res.entity.averageSectionRating,
      maxGoalRating: res.entity.maxSectionRating,
      employee: res.entity.employee,
    };
  });

export const getBehaviorFormV1 = ({ reviewId, employeeId, providerType, isPreview, previewSubmissionStatus }) =>
  getReviewFormApiV1({
    formName: REVIEW_FORMS_TYPE_MAP.COMPETENCY,
    reviewId,
    employeeId,
    providerType,
    isPreview,
    previewSubmissionStatus,
  }).then((res) => {
    const mappedResponse = res.entity.competencyQuestions.map(({ competency, questions }) => {
      const tag = new HashTag();

      tag.fromJSON(competency.orgTag);
      tag.addStatsFromJSON(competency.stats);

      return {
        tag,
        questions: questions.map((question) => ReviewQuestion.fromJSON(question)),
      };
    });
    return {
      competencyQuestions: mappedResponse,
      averageCompetencyRating: res.entity.averageSectionRating,
      maxCompetencyRating: res.entity.maxSectionRating,
      employee: res.entity.employee,
    };
  });

export const getQAFormV1 = ({ reviewId, employeeId, providerType, isPreview, previewSubmissionStatus }) =>
  getReviewFormApiV1({
    formName: REVIEW_FORMS_TYPE_MAP.QNA,
    reviewId,
    employeeId,
    providerType,
    isPreview,
    previewSubmissionStatus,
  }).then((res) => {
    const mappedResponse = res.entity.qnaQuestions.map((question) => ReviewQuestion.fromJSON(question));
    return {
      QNAQuestions: mappedResponse,
      averageQNARating: res.entity.averageSectionRating,
      maxQNARating: res.entity.maxSectionRating,
      employee: res.entity.employee,
    };
  });

export const getReviewPeriodSummaryGoals = ({ reviewId, employeeId }) =>
  getReviewPeriodSummaryGoalsApi({
    reviewId,
    employeeId,
  }).then((res) =>
    res.entity.map(({ computedData, parentGoalBasicDetails, weightage }) => ({
      ...computedData,
      parentGoalBasicDetails,
      weightage,
    }))
  );

export const getPerformancePreview = ({ empId, type }) =>
  getPerformancePreviewApi({
    empId,
  }).then((res) => {
    const updatedResponse = PerformancePreviewGridBox.fromJSON(res.entity, type);
    const flippedRatingMap = objectFlip(updatedResponse.ratingMap);
    const ratingDetails = [...updatedResponse.ratingDetails.pastRatingList];
    if (updatedResponse.ratingDetails.currentPredictedRating) {
      ratingDetails.push(updatedResponse.ratingDetails.currentPredictedRating);
    }
    const expandedRatingDetails = [...updatedResponse.expandedViewRatingDetails.pastRatingList];
    if (updatedResponse.ratingDetails.currentPredictedRating) {
      expandedRatingDetails.push(updatedResponse.ratingDetails.currentPredictedRating);
    }
    const newReviewRatingDetailsResponseList = ratingDetails.map((response) => {
      const overallRating = Number.isNaN(response.overallRating)
        ? response.overallRating
        : Number(response.overallRating);
      const mappedRating = updatedResponse.ratingMap[overallRating];
      return {
        ...response,
        numOverallRating: mappedRating || overallRating,
      };
    });
    const newExpandedReviewRatingDetailsResponseList = expandedRatingDetails.map((response) => {
      const overallRating = Number.isNaN(response.overallRating)
        ? response.overallRating
        : Number(response.overallRating);
      const mappedRating = updatedResponse.ratingMap[overallRating];
      return {
        ...response,
        numOverallRating: mappedRating || overallRating,
      };
    });
    return {
      chartData: updatedResponse.chartData,
      ratingMap: flippedRatingMap,
      reviewRatingDetailsResponseList: newReviewRatingDetailsResponseList,
      dataMap: RATINGS_TYPES,
      expandedViewRatingDetails: newExpandedReviewRatingDetailsResponseList,
      isShowPerformancePreviewGraph: res?.entity?.showPerformancePreviewGraph,
    };
  });

export function updateReviewGoalsWeightage({ employeeId, formId, body }) {
  return updateReviewGoalsWeightageApi({
    employeeId,
    formId,
    body,
  }).then((res) => res.entity);
}

export const getFormatFeedbackFormText = ({
  reviewId,
  empId,
  questionId,
  groupId,
  revisionNumber,
  feedbackText,
  rephraseInstruction,
}) =>
  getFormatFeedbackFormTextApi({
    reviewId,
    empId,
    questionId,
    groupId,
    revisionNumber,
    feedbackText,
    rephraseInstruction,
  }).then((res) => res.entity);

export const getTextAnalysis = ({ reviewId, empId, groupId, revisionNumber, feedbackText }) =>
  getTextAnalysisApi({
    reviewId,
    empId,
    groupId,
    revisionNumber,
    feedbackText,
  }).then((res) => res.entity);

// export const getAIManagerSummary = ({
//   reviewId,
//   empId,
//   questionId,
//   goalId,
//   goalText,
//   goalProgressLevel,
//   competencyText,
//   competencyDescription,
//   competencyId
// }) => {
//   return getAIManagerSummaryApi({
//     reviewId,
//     empId,
//     questionId,
//     goalId,
//     goalText,
//     goalProgressLevel,
//     competencyText,
//     competencyDescription,
//     competencyId
//   }).then((res) => res.entity);
//   // }).then((res) => ({
//   //   is_success: true,
//   //   responseItems: [
//   //     {
//   //       employee: {
//   //         id: 14722,
//   //         displayName: 'Ankur Singh',
//   //         profilePhoto:
//   //           'https://mesh-media.s3.ap-south-1.amazonaws.com/mesh/media/profilePhoto/defaultImages/defaultPhoto-13.png',
//   //         designation: 'SDE',
//   //         ownerType: null,
//   //         employeeFunction: 'Brawn'
//   //       },
//   //       providerType: 'SELF',
//   //       selectedAnswerOptions: [
//   //         { id: 43780, description: '2', score: 1.0, displayOrder: 2, questionId: 63618 }
//   //       ],
//   //       feedbackText: 'common question devliver ui',
//   //       feedbackTextHtml: '<p>common question devliver ui</p>',
//   //       answerType: 'DROP_DOWN'
//   //     }
//   //   ],
//   //   summaryPoints: ['hello', 'hi'],
//   //   summaryReviews: ['sldfjlks dfklj sdkfljsdkl fklsdjf lksdj fklsdjf '],
//   //   summary:
//   //     '<ul><li><p>Regularly gave updates on status and progress on tasks assigned</p></li><li><p>Worked hard on goals and completed assigned tasks on time</p></li><li><p>Quickly delivered updates and completed Salesforce implementation on time</p></li></ul>'
//   // }));
// };

export const getAIManagerSummaryPoints = ({
  reviewId,
  empId,
  questionId,
  goalId,
  goalText,
  goalProgressLevel,
  competencyText,
  competencyDescription,
  competencyId,
}) =>
  getAIManagerSummaryPointsApi({
    reviewId,
    empId,
    questionId,
    goalId,
    goalText,
    goalProgressLevel,
    competencyText,
    competencyDescription,
    competencyId,
  });

export const postAIManagerSummaryFeedback = ({
  questionId,
  responseId,
  isLike,
  feedbackComment,
  summaryPoints,
  summaryReviews,
}) =>
  postAIManagerSummaryFeedbackApi({
    questionId,
    responseId,
    isLike,
    feedbackComment,
    summaryPoints,
    summaryReviews,
  }).then((res) => res.entity);
